import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Landmark, Users, Heart, BookOpen, Globe, MessageCircle, Star, CheckCircle, Sparkles, Award, Camera, GraduationCap, Handshake, Crown, Building2 } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ScrollProgress } from '../components/ScrollProgress';
import { LanguageProvider } from '../context/LanguageContext';
export function PoliticiansPage() {
  return <LanguageProvider data-id="element-1846">
      <div className="relative bg-black text-white overflow-x-hidden" data-id="element-1847">
        <ScrollProgress data-id="element-1848" />
        <Navbar data-id="element-1849" />
        <main className="w-full" data-id="element-1850">
          <ConstituencyHero data-id="element-1851" />
          <WhyItMattersSection data-id="element-1852" />
          <TheExperienceSection data-id="element-1853" />
          <InvolvementOptionsSection data-id="element-1854" />
          <ConstituencyNightSection data-id="element-1855" />
          <ConstituencyCTA data-id="element-1856" />
        </main>
        <Footer data-id="element-1857" />
      </div>
    </LanguageProvider>;
}
function ConstituencyHero() {
  return <section className="relative w-full min-h-screen bg-black flex items-center justify-center overflow-hidden" data-id="element-1858">
      <div className="absolute inset-0" data-id="element-1859">
        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/pxKq2k9j3qcBE1TheAWARz/Experience_image.png" alt="Life in the Sikh Kingdoms" className="w-full h-full object-cover opacity-25" data-id="element-1860" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black" data-id="element-1861"></div>
      </div>

      <div className="relative z-10 text-center px-4 max-w-5xl mx-auto" data-id="element-1862">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.1
      }} data-id="element-1863">
          <Link to="/" className="inline-flex items-center gap-2 text-amber-400/70 hover:text-amber-400 text-sm tracking-wider uppercase font-light mb-12 transition-colors" data-id="element-1864">
            <ArrowLeft className="w-4 h-4" data-id="element-1865" />
            Back to Life in the Sikh Kingdoms
          </Link>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 0.2
      }} data-id="element-1866">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6 font-light" data-id="element-1867">
            Presented by Akal Steel
          </p>
          <p className="text-amber-400 tracking-[0.3em] text-xs md:text-sm uppercase mb-8 font-light" data-id="element-1868">
            Constituency &amp; Cultural Night
          </p>
          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl text-white mb-8 tracking-tight leading-none" data-id="element-1869">
            COMMUNITY &amp;
            <br data-id="element-1870" />
            <span className="text-amber-400" data-id="element-1871">CONSTITUENCY NIGHT</span>
          </h1>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-1872"></div>
          <p className="text-lg md:text-xl text-white/70 font-light max-w-3xl mx-auto leading-relaxed" data-id="element-1873">
            A Nonpartisan Cultural Celebration of Heritage, Education &amp;
            Multicultural Excellence — A Community Economic Win for Your Riding
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.6
      }} className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-8 text-white/60" data-id="element-1874">
          <div className="text-center" data-id="element-1875">
            <p className="text-sm tracking-[0.2em] uppercase mb-1 text-amber-400" data-id="element-1876">
              Heritage
            </p>
            <p className="font-serif text-xl text-white" data-id="element-1877">Education</p>
            <p className="text-xs font-light" data-id="element-1878">Cultural Preservation</p>
          </div>
          <div className="hidden sm:block w-px h-12 bg-white/20" data-id="element-1879"></div>
          <div className="text-center" data-id="element-1880">
            <p className="text-sm tracking-[0.2em] uppercase mb-1 text-amber-400" data-id="element-1881">
              Arts
            </p>
            <p className="font-serif text-xl text-white" data-id="element-1882">Innovation</p>
            <p className="text-xs font-light" data-id="element-1883">Multicultural Excellence</p>
          </div>
          <div className="hidden sm:block w-px h-12 bg-white/20" data-id="element-1884"></div>
          <div className="text-center" data-id="element-1885">
            <p className="text-sm tracking-[0.2em] uppercase mb-1 text-amber-400" data-id="element-1886">
              Tourism
            </p>
            <p className="font-serif text-xl text-white" data-id="element-1887">City Pride</p>
            <p className="text-xs font-light" data-id="element-1888">Community Economic Win</p>
          </div>
        </motion.div>
      </div>

      <motion.div className="absolute bottom-8 left-1/2 transform -translate-x-1/2" animate={{
      y: [0, 10, 0]
    }} transition={{
      duration: 2,
      repeat: Infinity
    }} data-id="element-1889">
        <div className="w-px h-16 bg-gradient-to-b from-transparent via-white/50 to-transparent" data-id="element-1890"></div>
      </motion.div>
    </section>;
}
function WhyItMattersSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-1891">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-950/10 to-black z-0" data-id="element-1892"></div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-1893">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-1894">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-1895">
            The Civic Case
          </p>
          <h2 className="font-serif text-5xl md:text-6xl text-white mb-8" data-id="element-1896">
            WHY IT <span className="text-amber-400" data-id="element-1897">MATTERS</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-1898"></div>
          <p className="text-xl text-white/70 max-w-3xl mx-auto font-light leading-relaxed" data-id="element-1899">
            For MPs, MPPs, councillors, and community leaders, this is a rare
            nonpartisan opportunity to champion heritage, education, and
            multicultural excellence in your riding.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-1900">
          {[{
          icon: <BookOpen className="w-8 h-8 text-amber-400 mb-4" data-id="element-1901" />,
          title: 'Heritage & Education',
          body: 'A living classroom connecting youth, seniors, and newcomers to 200+ years of Sikh history — through taste, sight, sound, and storytelling. Preserving culture through innovation.'
        }, {
          icon: <Globe className="w-8 h-8 text-amber-400 mb-4" data-id="element-1902" />,
          title: 'Multicultural Excellence',
          body: "Showcasing South Asian culture as a cornerstone of Canada's multicultural identity. An event that brings diaspora leaders, interfaith communities, and newcomers together under one roof."
        }, {
          icon: <Award className="w-8 h-8 text-amber-400 mb-4" data-id="element-1903" />,
          title: 'Arts Innovation',
          body: 'Oscar-winning animation, immersive dining, and interactive technology — pioneering a new form of cultural experience that positions your riding at the forefront of arts and culture in Canada.'
        }, {
          icon: <Landmark className="w-8 h-8 text-amber-400 mb-4" data-id="element-1904" />,
          title: 'Tourism & City Pride',
          body: 'Positioning Toronto as a global destination for innovative cultural experiences. Generates local economic activity, media coverage, and community pride — a tangible win for your riding.'
        }].map((card, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: i * 0.1
        }} className="border-2 border-amber-400/30 p-8 bg-black/40" data-id="element-1905">
              {card.icon}
              <h3 className="font-serif text-2xl text-amber-400 mb-3" data-id="element-1906">
                {card.title}
              </h3>
              <p className="text-white/70 font-light leading-relaxed" data-id="element-1907">
                {card.body}
              </p>
            </motion.div>)}
        </div>
      </div>
    </motion.section>;
}
function TheExperienceSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-1908">
      <div className="container mx-auto px-4" data-id="element-1909">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-1910">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-1911">
            What Your Guests Will Experience
          </p>
          <h2 className="font-serif text-5xl md:text-6xl text-white mb-8" data-id="element-1912">
            THE <span className="text-amber-400" data-id="element-1913">EXPERIENCE</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto" data-id="element-1914"></div>
        </motion.div>

        <div className="max-w-6xl mx-auto" data-id="element-1915">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1916">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-1917">
              <div className="aspect-video relative overflow-hidden" data-id="element-1918">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/rru8mxg1f864Swgj1TtDBp/Gemini_Generated_Image_xf598lxf598lxf59.png" alt="Immersive Experience" className="w-full h-full object-cover" data-id="element-1919" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" data-id="element-1920"></div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="space-y-6" data-id="element-1921">
              <p className="text-white/80 text-lg leading-relaxed font-light" data-id="element-1922">
                Life in the Sikh Kingdoms is an immersive dining and cultural
                experience that brings Maharaja Ranjit Singh's era to life
                through taste, sight, sound, and storytelling — at Toronto's
                Illuminarium.
              </p>
              <p className="text-white/70 leading-relaxed font-light" data-id="element-1923">
                This is not theater. This is not dining. This is a portal to
                1825 Lahore — a nonpartisan cultural institution celebrating
                heritage, education, multicultural excellence, arts innovation,
                and city pride.
              </p>

              <div className="border-l-2 border-amber-400/30 pl-6 space-y-4" data-id="element-1924">
                {['5–7 courses synchronized with immersive projections', '360° visual storytelling across walls, floor, and tables', 'Royal Punjabi cuisine from world-renowned chefs', '90 minutes of complete cultural immersion', 'Interactive technology — voice, motion, and scent'].map((item, i) => <div key={i} className="flex items-start" data-id="element-1925">
                    <span className="text-amber-400 mr-3 mt-1" data-id="element-1926">—</span>
                    <span className="text-white/70 font-light" data-id="element-1927">{item}</span>
                  </div>)}
              </div>

              <div className="border border-amber-400/30 p-6 bg-black/40" data-id="element-1928">
                <div className="grid grid-cols-3 gap-4 text-center" data-id="element-1929">
                  <div data-id="element-1930">
                    <p className="text-amber-400 text-xs tracking-wider uppercase mb-1" data-id="element-1931">
                      Venue
                    </p>
                    <p className="text-white font-light text-sm" data-id="element-1932">
                      Illuminarium Toronto
                    </p>
                  </div>
                  <div data-id="element-1933">
                    <p className="text-amber-400 text-xs tracking-wider uppercase mb-1" data-id="element-1934">
                      Date
                    </p>
                    <p className="text-white font-light text-sm" data-id="element-1935">Spring 2026</p>
                  </div>
                  <div data-id="element-1936">
                    <p className="text-amber-400 text-xs tracking-wider uppercase mb-1" data-id="element-1937">
                      Capacity
                    </p>
                    <p className="text-white font-light text-sm" data-id="element-1938">
                      Up to 120 Guests
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-2 border-amber-400/40 p-6 bg-gradient-to-br from-amber-950/20 to-black/60" data-id="element-1939">
                <p className="text-white/70 font-light text-sm leading-relaxed" data-id="element-1940">
                  <span className="text-amber-400 font-medium" data-id="element-1941">
                    ROM Legacy:{' '}
                  </span>
                  This experience honors the groundbreaking "Art of the Sikh
                  Kingdoms" exhibition at the Royal Ontario Museum —{' '}
                  <strong className="text-white" data-id="element-1942">
                    the second most popular exhibition in ROM history
                  </strong>
                  .
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.section>;
}
function InvolvementOptionsSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-1943">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-950/10 to-black z-0" data-id="element-1944"></div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-1945">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-1946">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-1947">
            Simple Menu of Options
          </p>
          <h2 className="font-serif text-5xl md:text-6xl text-white mb-8" data-id="element-1948">
            WAYS TO <span className="text-amber-400" data-id="element-1949">GET INVOLVED</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-1950"></div>
          <p className="text-white/70 font-light max-w-2xl mx-auto" data-id="element-1951">
            Whether you're an MP, MPP, city councillor, or community leader —
            choose the level of involvement that fits your schedule and
            priorities.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-1952">
          {/* Option A */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0
        }} className="relative" data-id="element-1953">
            <div className="absolute top-0 left-0 right-0 flex justify-center z-30 transform -translate-y-1/2" data-id="element-1954">
              <div className="bg-amber-400 text-black font-bold py-1.5 px-5 flex items-center gap-2" data-id="element-1955">
                <Star className="w-3 h-3" fill="currentColor" data-id="element-1956" />
                <span className="text-xs uppercase tracking-wider" data-id="element-1957">
                  Recommended
                </span>
              </div>
            </div>
            <div className="border-2 border-amber-400 bg-black/80 p-8 pt-10 h-full" data-id="element-1958">
              <div className="flex items-center gap-3 mb-4" data-id="element-1959">
                <span className="font-serif text-3xl text-amber-400/40" data-id="element-1960">A</span>
                <h3 className="font-serif text-2xl text-white" data-id="element-1961">
                  Patron of the Night
                </h3>
              </div>
              <p className="text-amber-400/70 text-xs tracking-wider uppercase mb-4 font-light" data-id="element-1962">
                Constituency Night Host
              </p>
              <ul className="space-y-3" data-id="element-1963">
                {['Host the Constituency Night as Guest of Honour', 'Short welcome remarks (5–7 minutes)', 'Photo moment + community recognition segment', 'Press photo call with community leaders'].map((item, i) => <li key={i} className="flex items-start text-white/70 text-sm font-light" data-id="element-1964">
                    <CheckCircle className="w-4 h-4 text-amber-400 mr-3 mt-0.5 flex-shrink-0" data-id="element-1965" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </motion.div>

          {/* Option B */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} data-id="element-1966">
            <div className="border-2 border-amber-400/30 bg-black/80 p-8 h-full" data-id="element-1967">
              <div className="flex items-center gap-3 mb-4" data-id="element-1968">
                <span className="font-serif text-3xl text-amber-400/40" data-id="element-1969">B</span>
                <h3 className="font-serif text-2xl text-white" data-id="element-1970">
                  Cultural Roundtable
                </h3>
              </div>
              <p className="text-amber-400/70 text-xs tracking-wider uppercase mb-4 font-light" data-id="element-1971">
                Community Leader Convening
              </p>
              <ul className="space-y-3" data-id="element-1972">
                {['Private preview for community leaders & sponsors', 'Convene diaspora, cultural orgs, and business leaders', 'Sponsorship ladder pitch opportunity'].map((item, i) => <li key={i} className="flex items-start text-white/70 text-sm font-light" data-id="element-1973">
                    <CheckCircle className="w-4 h-4 text-amber-400 mr-3 mt-0.5 flex-shrink-0" data-id="element-1974" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </motion.div>

          {/* Option C */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-1975">
            <div className="border-2 border-amber-400/30 bg-black/80 p-8 h-full" data-id="element-1976">
              <div className="flex items-center gap-3 mb-4" data-id="element-1977">
                <span className="font-serif text-3xl text-amber-400/40" data-id="element-1978">C</span>
                <h3 className="font-serif text-2xl text-white" data-id="element-1979">
                  Youth Heritage Night
                </h3>
              </div>
              <p className="text-amber-400/70 text-xs tracking-wider uppercase mb-4 font-light" data-id="element-1980">
                Education &amp; Legacy
              </p>
              <ul className="space-y-3" data-id="element-1981">
                {['Student tickets + mentorship tie-in', 'Educator guide + talkback session', 'Highlight youth & heritage mandate'].map((item, i) => <li key={i} className="flex items-start text-white/70 text-sm font-light" data-id="element-1982">
                    <CheckCircle className="w-4 h-4 text-amber-400 mr-3 mt-0.5 flex-shrink-0" data-id="element-1983" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </motion.div>

          {/* Option D */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} data-id="element-1984">
            <div className="border-2 border-amber-400/30 bg-black/80 p-8 h-full" data-id="element-1985">
              <div className="flex items-center gap-3 mb-4" data-id="element-1986">
                <span className="font-serif text-3xl text-amber-400/40" data-id="element-1987">D</span>
                <h3 className="font-serif text-2xl text-white" data-id="element-1988">
                  Sponsor Drive
                </h3>
              </div>
              <p className="text-amber-400/70 text-xs tracking-wider uppercase mb-4 font-light" data-id="element-1989">
                Business Community Activation
              </p>
              <ul className="space-y-3" data-id="element-1990">
                {['Introduce 5–10 local sponsor prospects', 'Warm intros to local businesses in your riding', 'We close the deals — minimal time required'].map((item, i) => <li key={i} className="flex items-start text-white/70 text-sm font-light" data-id="element-1991">
                    <CheckCircle className="w-4 h-4 text-amber-400 mr-3 mt-0.5 flex-shrink-0" data-id="element-1992" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>;
}
function ConstituencyNightSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-1993">
      <div className="container mx-auto px-4" data-id="element-1994">
        <div className="max-w-5xl mx-auto" data-id="element-1995">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="text-center mb-16" data-id="element-1996">
            <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-1997">
              The Vision
            </p>
            <h2 className="font-serif text-5xl md:text-6xl text-white mb-8" data-id="element-1998">
              CONSTITUENCY NIGHT{' '}
              <span className="text-amber-400" data-id="element-1999">DONE RIGHT</span>
            </h2>
            <div className="w-24 h-px bg-amber-400/30 mx-auto" data-id="element-2000"></div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start" data-id="element-2001">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-2002">
              <div className="border-2 border-amber-400/40 p-8 md:p-10 bg-gradient-to-br from-amber-950/20 to-black/60" data-id="element-2003">
                <h3 className="font-serif text-2xl text-amber-400 mb-6" data-id="element-2004">
                  What It Looks Like
                </h3>
                <div className="space-y-5" data-id="element-2005">
                  {[{
                  icon: <Crown className="w-4 h-4 text-amber-400" data-id="element-2006" />,
                  title: 'Guest of Honour Welcome',
                  sub: '5–7 minute opening remarks from the elected official or community leader'
                }, {
                  icon: <Star className="w-4 h-4 text-amber-400" data-id="element-2007" />,
                  title: 'Recognition of Community Builders',
                  sub: 'Acknowledge local leaders, volunteers, and cultural champions'
                }, {
                  icon: <Heart className="w-4 h-4 text-amber-400" data-id="element-2008" />,
                  title: 'Optional Nonprofit Tie-In',
                  sub: 'Keeps it civic, not political — fundraising for ROM Sikh gallery'
                }, {
                  icon: <Users className="w-4 h-4 text-amber-400" data-id="element-2009" />,
                  title: 'Group Tickets for Local Orgs',
                  sub: 'Youth seats sponsored by a local business — full room guaranteed'
                }, {
                  icon: <Camera className="w-4 h-4 text-amber-400" data-id="element-2010" />,
                  title: 'Press Photo Call',
                  sub: 'Professional photography, ethnic media coverage, community papers'
                }].map((item, i) => <div key={i} className="flex items-start gap-4" data-id="element-2011">
                      <div className="w-8 h-8 flex items-center justify-center border border-amber-400/30 flex-shrink-0 mt-0.5" data-id="element-2012">
                        {item.icon}
                      </div>
                      <div data-id="element-2013">
                        <p className="text-white font-light text-sm mb-1" data-id="element-2014">
                          {item.title}
                        </p>
                        <p className="text-white/50 text-xs font-light" data-id="element-2015">
                          {item.sub}
                        </p>
                      </div>
                    </div>)}
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="space-y-6" data-id="element-2016">
              <div className="border border-amber-400/30 p-6 bg-black/40" data-id="element-2017">
                <h4 className="font-serif text-xl text-amber-400 mb-4" data-id="element-2018">
                  The Result
                </h4>
                <div className="space-y-3" data-id="element-2019">
                  {['Full room with engaged community members', 'Strong press photos for all parties', 'Goodwill across diaspora, cultural, and civic communities', 'Sponsor confidence from dignitary presence', 'Media coverage in ethnic and community press'].map((item, i) => <div key={i} className="flex items-center gap-3" data-id="element-2020">
                      <CheckCircle className="w-4 h-4 text-amber-400 flex-shrink-0" data-id="element-2021" />
                      <span className="text-white/70 text-sm font-light" data-id="element-2022">
                        {item}
                      </span>
                    </div>)}
                </div>
              </div>

              <div className="border-2 border-amber-400/40 p-6 bg-gradient-to-br from-amber-950/20 to-black/60" data-id="element-2023">
                <p className="text-amber-400 text-xs tracking-wider uppercase mb-3 font-light" data-id="element-2024">
                  Who Gets Invited
                </p>
                <div className="space-y-2 text-white/60 text-sm font-light" data-id="element-2025">
                  <p data-id="element-2026">— Diaspora leaders &amp; cultural organizations</p>
                  <p data-id="element-2027">— School boards &amp; newcomer services</p>
                  <p data-id="element-2028">— Business improvement areas (BIAs)</p>
                  <p data-id="element-2029">— Tourism groups &amp; arts councils</p>
                  <p data-id="element-2030">— Local sponsors (banks, telcos, hotels)</p>
                  <p data-id="element-2031">— Consular reps &amp; interfaith leaders</p>
                </div>
              </div>

              <div className="border-2 border-amber-400/40 p-6 bg-gradient-to-br from-amber-950/20 to-black/60" data-id="element-2032">
                <Heart className="w-6 h-6 text-amber-400 mb-3" data-id="element-2033" />
                <p className="text-amber-400 italic font-light text-sm leading-relaxed" data-id="element-2034">
                  "A portion of all proceeds goes directly to the Sikh Art and
                  Culture Gallery at the Royal Ontario Museum, preserving Sikh
                  heritage for future generations."
                </p>
                <p className="text-white/40 text-xs font-light mt-3 tracking-wider uppercase" data-id="element-2035">
                  Every ticket funds cultural storytelling + community uplift
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.section>;
}
function ConstituencyCTA() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-2036">
      <div className="container mx-auto px-4" data-id="element-2037">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="max-w-3xl mx-auto text-center" data-id="element-2038">
          <Landmark className="w-10 h-10 text-amber-400 mx-auto mb-6" data-id="element-2039" />
          <h2 className="font-serif text-4xl md:text-5xl text-white mb-6" data-id="element-2040">
            Let's Build Something{' '}
            <span className="text-amber-400" data-id="element-2041">Meaningful Together</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-2042"></div>
          <p className="text-xl text-white/70 font-light mb-4 leading-relaxed" data-id="element-2043">
            We welcome the opportunity to discuss how this cultural experience
            can serve your constituency and community.
          </p>
          <p className="text-white/50 font-light mb-10 leading-relaxed" data-id="element-2044">
            This is a nonpartisan cultural institution open to MPs, MPPs, city
            councillors, and community leaders of all backgrounds. Our goal is
            to celebrate heritage, support education, and create a community
            economic win for your riding — together.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2045">
            <motion.a href="https://wa.me/14169998778?text=I%20am%20interested%20in%20discussing%20a%20constituency%20night%20for%20Life%20in%20the%20Sikh%20Kingdoms" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 bg-amber-400 hover:bg-amber-500 text-black px-8 py-4 font-medium tracking-wider uppercase text-sm transition-colors duration-300" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} data-id="element-2046">
              <MessageCircle className="w-5 h-5" data-id="element-2047" />
              Start the Conversation
            </motion.a>
            <Link to="/" className="inline-flex items-center justify-center gap-2 border-2 border-amber-400/50 hover:border-amber-400 text-white hover:text-amber-400 px-8 py-4 font-light tracking-wider uppercase text-sm transition-all duration-300" data-id="element-2048">
              <ArrowLeft className="w-4 h-4" data-id="element-2049" />
              Back to Main Site
            </Link>
          </div>
          <p className="text-white/40 text-sm font-light mt-6" data-id="element-2050">
            Our team will respond within 24 hours to discuss partnership
            opportunities.
          </p>
        </motion.div>
      </div>
    </motion.section>;
}